import { defineStore } from "pinia"
import { defaultDriverWorkspaceRoute, normalizeDriverCapabilities, resolveDriverWorkspaceType } from "~/utils/driverWorkspace"

const extractAuthUser = (payload) => {
  if (payload && typeof payload === "object") {
    if (payload.user && typeof payload.user === "object") {
      return payload.user
    }

    if (payload.id && payload.role) {
      return payload
    }
  }

  return null
}

export const useAuthStore = defineStore("auth", {
  state: () => ({
    user: null,
    token: null,
    isAuthenticated: false,
    isInitialized: false,
    isUpdatingStatus: false,
    isLoggingOut: false,
    isSwitchingWorkspace: false,
  }),

  getters: {
    // Role-based checks
    isAdmin: (state) => state.user?.role === "admin",
    isShipper: (state) => state.user?.role === "shipper",
    isCarrier: (state) => state.user?.role === "carrier",
    isDriver: (state) => state.user?.role === "driver",
    isForwarder: (state) => state.user?.role === "forwarder",
    isWarehouseOperator: (state) => state.user?.role === "warehouse_operator",
    isMover: (state) => state.user?.role === "mover_company" || state.user?.role === "mover",

    // Get user details based on role
    userDetails: (state) => state.user?.details || null,

    userRole: (state) => state.user?.role || null,
    userStatus: (state) => state.user?.status || null,

    // Company/Approval status getters
    companyStatus: (state) => {
      if (!state.user?.details) return null

      // For forwarders and carriers, get approval status from details
      if (state.user.role === "forwarder" && state.user.details.approval_status) {
        return state.user.details.approval_status
      }
      if (state.user.role === "carrier" && state.user.details.approval_status) {
        return state.user.details.approval_status
      }
      // For drivers, get approval status from details
      if (state.user.role === "driver" && state.user.details.approval_status) {
        return state.user.details.approval_status
      }
      if (state.user.role === "warehouse_operator") {
        return state.user.details.verification_status || state.user.details.approval_status || null
      }
      if ((state.user.role === "mover_company" || state.user.role === "mover") && state.user.details.approval_status) {
        return state.user.details.approval_status
      }

      return null
    },

    // Verification document status getters
    verificationDocuments: (state) => {
      if (!state.user?.details?.documents) return []
      return state.user.details.documents.filter((doc) => doc.is_verification_document === true)
    },

    pendingVerificationDocuments: (state) => {
      if (!state.user?.details?.documents) return []
      return state.user.details.documents.filter((doc) => doc.is_verification_document === true && doc.verification_status === "pending")
    },

    approvedVerificationDocuments: (state) => {
      if (!state.user?.details?.documents) return []
      return state.user.details.documents.filter((doc) => doc.is_verification_document === true && doc.verification_status === "approved")
    },

    rejectedVerificationDocuments: (state) => {
      if (!state.user?.details?.documents) return []
      return state.user.details.documents.filter((doc) => doc.is_verification_document === true && doc.verification_status === "rejected")
    },

    expiringVerificationDocuments: (state) => {
      if (!state.user?.details?.documents) return []
      const thirtyDaysFromNow = new Date()
      thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30)

      return state.user.details.documents.filter((doc) => {
        if (!doc.is_verification_document || !doc.expiry_date) return false
        const expiryDate = new Date(doc.expiry_date)
        return expiryDate <= thirtyDaysFromNow && expiryDate > new Date()
      })
    },

    expiredVerificationDocuments: (state) => {
      if (!state.user?.details?.documents) return []
      const now = new Date()

      return state.user.details.documents.filter((doc) => {
        if (!doc.is_verification_document || !doc.expiry_date) return false
        const expiryDate = new Date(doc.expiry_date)
        return expiryDate < now
      })
    },

    // Overall verification status
    hasPendingVerificationDocuments: (state) => {
      return state.user?.details?.documents?.some((doc) => doc.is_verification_document === true && doc.verification_status === "pending") || false
    },

    hasRejectedVerificationDocuments: (state) => {
      return state.user?.details?.documents?.some((doc) => doc.is_verification_document === true && doc.verification_status === "rejected") || false
    },

    hasExpiringVerificationDocuments: (state) => {
      if (!state.user?.details?.documents) return false
      const thirtyDaysFromNow = new Date()
      thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30)

      return state.user.details.documents.some((doc) => {
        if (!doc.is_verification_document || !doc.expiry_date) return false
        const expiryDate = new Date(doc.expiry_date)
        return expiryDate <= thirtyDaysFromNow && expiryDate > new Date()
      })
    },

    driverWorkspaceType: (state) => resolveDriverWorkspaceType(state.user),
    driverCapabilities: (state) => normalizeDriverCapabilities(state.user),
    canAccessCityXpress: (state) => normalizeDriverCapabilities(state.user).cityxpress_enabled,
    canAccessInTransit: (state) => normalizeDriverCapabilities(state.user).intransit_enabled,
    canAccessCargo: (state) => normalizeDriverCapabilities(state.user).cargo_enabled,
    canManageVehicle: (state) => normalizeDriverCapabilities(state.user).can_manage_vehicle,
    defaultDriverWorkspaceRoute: (state) => defaultDriverWorkspaceRoute(state.user),
    activeDriverProfile: (state) => state.user?.active_profile || null,
    availableDriverProfiles: (state) => (Array.isArray(state.user?.available_profiles) ? state.user.available_profiles : []),
    hasMultipleDriverProfiles: (state) => (Array.isArray(state.user?.available_profiles) ? state.user.available_profiles.length > 1 : false),
  },

  actions: {
    /**
     * Get axios instance (handles early initialization before pinia plugin runs)
     */
    getAxios() {
      return this.$axios || useNuxtApp().$axios
    },

    async checkAuth() {
      try {
        const response = await this.getAxios().get("/api/v1/authenticated/user", { credentials: "include" })

        const user = extractAuthUser(response.data)
        if (!user) {
          this.user = null
          this.isAuthenticated = false
          return false
        }

        this.user = user
        this.isAuthenticated = true
        if (this.user.role === "driver") {
          const { startTracking } = useLocationTracker()
          startTracking()
        }
        return true
      } catch (error) {
        this.user = null
        this.isAuthenticated = false
        // Return the error object so we can check for network issues
        return error
      }
    },
    async register(userData) {
      try {
        // Using this.$axios injected by pinia plugin

        await this.getAxios().get("/sanctum/csrf-cookie")

        const response = await this.getAxios().post("/api/v1/auth/register", userData)

        if (response.data.success) {
          return {
            success: true,
            email: response.data.email,
            message: response.data.message,
          }
        }

        return {
          success: false,
          error: "Invalid response from server",
        }
      } catch (error) {
        const apiError = error.response?.data

        return {
          success: false,
          error: apiError?.message || "Registration failed.",
          errors: apiError?.errors || null,
        }
      }
    },

    async registerDriverWorkforce(userData, workforceType) {
      try {
        await this.getAxios().get("/sanctum/csrf-cookie")

        const endpoint =
          workforceType === "intransit"
            ? "/api/v1/auth/register/driver/intransit"
            : "/api/v1/auth/register/driver/cityxpress"

        const response = await this.getAxios().post(endpoint, userData)

        if (response.data.success) {
          return {
            success: true,
            email: response.data.email,
            message: response.data.message,
            onboarding_contract: response.data.onboarding_contract || null,
            intended_workforce_type: response.data.intended_workforce_type || null,
          }
        }

        return {
          success: false,
          error: "Invalid response from server",
        }
      } catch (error) {
        const apiError = error.response?.data

        return {
          success: false,
          error: apiError?.message || "Registration failed.",
          errors: apiError?.errors || null,
        }
      }
    },

    async login(credentials) {
      try {
        // First, get CSRF cookie
        await this.getAxios().get("/sanctum/csrf-cookie")

        // Attempt login
        const response = await this.getAxios().post("/api/v1/auth/login", credentials)

        // Our API returns token and user directly
        const { user } = response.data

        if (user) {
          this.setAuthData({ user })
          if (user.role === "driver") {
            const { startTracking } = useLocationTracker()
            startTracking()
          }
          return { success: true, user }
        }

        return {
          success: false,
          error: "Invalid response from server",
        }
      } catch (error) {
        console.log("error", error)
        const responseData = error.response?.data || {}

        return {
          success: false,
          error: responseData.message || "Login failed. Please check your credentials.",
          requires_verification: responseData.requires_verification || false,
          email: responseData.email || credentials.email,
        }
      }
    },

    async logout() {
      // Prevent multiple simultaneous logout calls

      this.isLoggingOut = true

      try {
        // Using this.$axios injected by pinia plugin

        console.log("logout started")

        // Get fresh CSRF cookie before logout

        await this.getAxios().get("/sanctum/csrf-cookie")

        // Attempt logout - ignore errors since we're logging out anyway
        try {
          await this.getAxios().post("/api/v1/authenticated/logout")
        } catch (logoutError) {
          // Ignore logout endpoint errors (including 401)
          console.log("Logout endpoint error (ignored):", logoutError.response?.status)
        }
      } catch (error) {
        console.error("Logout error:", error)
      } finally {
        // Always clear auth data regardless of API response
        if (this.user?.role === "driver") {
          const { stopTracking } = useLocationTracker()
          stopTracking()
        }
        this.clearAuthData()

        // Clear axios auth header
        // Using this.$axios injected by pinia plugin
        delete this.getAxios().defaults.headers.common["Authorization"]

        // Clear any persisted store data
        localStorage.removeItem("auth-store")

        // Reset logout flag
        this.isLoggingOut = false

        // Navigate to login page after clearing auth data
        navigateTo("/auth/login", { replace: true })
      }
    },

    async fetchUser() {
      try {
        // Using this.$axios injected by pinia plugin

        // Get fresh CSRF cookie
        await this.getAxios().get("/sanctum/csrf-cookie")

        const response = await this.getAxios().get("/api/v1/authenticated/user")
        const user = extractAuthUser(response.data)

        if (user?.id) {
          this.user = user
          this.isAuthenticated = true
          return true
        }
        return false
      } catch (error) {
        // console.error("Fetch user error:", error)
        this.clearAuthData()
        return false
      }
    },

    // Helper method to set authentication data
    setAuthData({ user }) {
      this.user = user
      this.isAuthenticated = true
      this.isInitialized = true

      // Store token in localStorage for persistence
      // localStorage.setItem("auth_token", token)

      // Set token in axios headers
      // Using this.$axios injected by pinia plugin
      // $axios.defaults.headers.common["Authorization"] = `Bearer ${token}`

      // Ensure axios sends cookies with requests
      const $axios = this.getAxios()
      if ($axios?.defaults) {
        $axios.defaults.withCredentials = true
      }
    },

    applyDriverWorkforcePayload(payload = {}) {
      if (!this.user || typeof payload !== "object" || payload === null) return

      const currentDetails = this.user.details && typeof this.user.details === "object" ? this.user.details : {}
      const activeProfile = payload.active_profile || this.user.active_profile || null
      const workforceContext = payload.workforce_context || this.user.workforce_context || currentDetails.workforce_context || null
      const capabilities = payload.capabilities || this.user.capabilities || this.user.service_capabilities || currentDetails.service_capabilities || null
      const activeWorkforceType = activeProfile?.type || workforceContext?.type || this.user.active_workforce_type || currentDetails.workforce_type || null

      this.user = {
        ...this.user,
        active_workforce_type: activeWorkforceType,
        workspace_type: payload.workspace_type || activeProfile?.workspace_type || this.user.workspace_type,
        active_profile: activeProfile,
        available_profiles: Array.isArray(payload.available_profiles) ? payload.available_profiles : this.user.available_profiles || [],
        workforce_context: workforceContext,
        capabilities,
        service_capabilities: capabilities,
        details: {
          ...currentDetails,
          workforce_type: activeWorkforceType,
          workforce_context: workforceContext,
          service_capabilities: capabilities,
        },
      }
    },

    // Helper method to clear authentication data
    clearAuthData() {
      this.user = null
      this.token = null
      this.isAuthenticated = false
      // Don't reset isInitialized here

      // Clear token from localStorage
      localStorage.removeItem("auth_token")

      // Remove token from axios headers
      // Using this.$axios injected by pinia plugin
      delete this.getAxios().defaults.headers.common["Authorization"]
    },

    // Initialize auth state from localStorage
    async initAuth() {
      if (this.isInitialized) return this.isAuthenticated

      if (process.client) {
        const token = localStorage.getItem("auth_token")
        if (token) {
          this.token = token

          // Using this.$axios injected by pinia plugin
          this.getAxios().defaults.headers.common["Authorization"] = `Bearer ${token}`
          this.getAxios().defaults.withCredentials = true

          const success = await this.fetchUser()
          this.isInitialized = true
          if (this.user.role === "driver") {
            const { startTracking } = useLocationTracker()
            startTracking()
          }
          return success
        }

        const sessionAuth = await this.checkAuth()
        this.isInitialized = true
        if (sessionAuth === true && this.user?.role === "driver") {
          const { startTracking } = useLocationTracker()
          startTracking()
        }
        return sessionAuth === true
      }

      this.isInitialized = true
      return false
    },

    // Password reset methods
    async forgotPassword(email) {
      try {
        // Using this.$axios injected by pinia plugin
        await this.getAxios().post("/api/v1/auth/forgot-password", { email })
        return { success: true, message: "Password reset link sent to email" }
      } catch (error) {
        // console.error("Forgot password error:", error)
        return {
          success: false,
          error: error.response?.data?.message || "Failed to send password reset link.",
        }
      }
    },

    async resetPassword(data) {
      try {
        // Using this.$axios injected by pinia plugin
        const response = await this.getAxios().post("/api/v1/auth/reset-password", data)
        return { success: true, message: response.data.message }
      } catch (error) {
        // console.error("Reset password error:", error)
        return {
          success: false,
          error: error.response?.data?.message || "Failed to reset password.",
        }
      }
    },
    async resendVerification(email) {
      try {
        // Using this.$axios injected by pinia plugin
        const response = await this.getAxios().post("/api/v1/auth/email/resend", { email })
        return { success: true, message: response.data.message }
      } catch (error) {
        // console.error("Resend verification error:", error)
        return {
          success: false,
          error: error.response?.data?.message || "Failed to resend verification email.",
        }
      }
    },

    async verifyEmail({ id, hash, signature, expires }) {
      try {
        // Using this.$axios injected by pinia plugin

        // Get CSRF cookie first
        await this.getAxios().get("/sanctum/csrf-cookie")

        // Verify email with all parameters
        const response = await this.getAxios().get(`/api/v1/auth/email/verify/${id}/${hash}`)

        return {
          success: true,
          message: response.data?.message || "Email verified successfully",
        }
      } catch (error) {
        // console.error("Email verification error:", error)
        return {
          success: false,
          error: error.response?.data?.message || "Failed to verify email.",
        }
      }
    },

    // Profile management methods
    async updateProfile(data) {
      try {
        // Using this.$axios injected by pinia plugin

        // Get CSRF cookie
        await this.getAxios().get("/sanctum/csrf-cookie")

        const response = await this.getAxios().post("/api/v1/profile/update", {
          name: data.name,
          phone: data.phone,
        })

        // Update user data in store
        if (response.data.user) {
          this.user = response.data.user
        }

        return {
          success: true,
          message: response.data.message || "Profile updated successfully",
        }
      } catch (error) {
        // console.error("Update profile error:", error)
        return {
          success: false,
          error: error.response?.data?.message || "Failed to update profile.",
        }
      }
    },

    async updatePassword(data) {
      try {
        // Using this.$axios injected by pinia plugin

        // Get CSRF cookie
        await this.getAxios().get("/sanctum/csrf-cookie")

        const response = await this.getAxios().post("/api/v1/profile/password", {
          current_password: data.currentPassword,
          password: data.newPassword,
          password_confirmation: data.confirmPassword,
        })

        return {
          success: true,
          message: response.data.message || "Password updated successfully",
        }
      } catch (error) {
        // console.error("Update password error:", error)
        return {
          success: false,
          error: error.response?.data?.message || "Failed to update password.",
        }
      }
    },

    async updateAvatar(file) {
      try {
        // Using this.$axios injected by pinia plugin

        // Get CSRF cookie
        await this.getAxios().get("/sanctum/csrf-cookie")

        const formData = new FormData()
        formData.append("avatar", file)

        const response = await this.getAxios().post("/api/v1/profile/avatar", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })

        // Update user data in store
        if (response.data.avatar_url) {
          this.user = {
            ...this.user,
            avatar: response.data.avatar_url,
          }
        }

        return {
          success: true,
          message: response.data.message || "Avatar updated successfully",
          avatar_url: response.data.avatar_url,
        }
      } catch (error) {
        // console.error("Update avatar error:", error)
        return {
          success: false,
          error: error.response?.data?.message || "Failed to update avatar.",
        }
      }
    },

    async removeAvatar() {
      try {
        // Using this.$axios injected by pinia plugin

        // Get CSRF cookie first
        await this.getAxios().get("/sanctum/csrf-cookie")

        const response = await this.getAxios().delete("/api/v1/profile/avatar")

        if (response.data.success) {
          // Update user data in store
          this.user = {
            ...this.user,
            avatar: null,
          }

          return {
            success: true,
            message: response.data.message || "Avatar removed successfully",
          }
        }

        return {
          success: false,
          error: "Failed to remove avatar",
        }
      } catch (error) {
        return {
          success: false,
          error: error.response?.data?.message || "Failed to remove avatar",
        }
      }
    },

    // Session Management Methods
    async fetchSessions() {
      try {
        // Using this.$axios injected by pinia plugin

        // Get CSRF cookie first
        await this.getAxios().get("/sanctum/csrf-cookie")

        const response = await this.getAxios().get("/api/v1/auth/sessions")

        const sessions = response.data.sessions.map((session) => ({
          id: session.id,
          deviceName: session.device_name || "Unknown Device",
          isCurrentDevice: session.is_current,
          createdAt: session.created_at,
          lastUsedAt: session.last_used_at,
        }))

        return {
          success: true,
          sessions,
        }
      } catch (error) {
        return {
          success: false,
          error: error.response?.data?.message || "Failed to fetch sessions",
        }
      }
    },

    async terminateSession(sessionId) {
      try {
        // Using this.$axios injected by pinia plugin

        // Get CSRF cookie first
        await this.getAxios().get("/sanctum/csrf-cookie")

        const response = await this.getAxios().delete(`/api/v1/auth/sessions/${sessionId}`)
        return {
          success: true,
          message: response.data.message || "Session terminated successfully",
        }
      } catch (error) {
        return {
          success: false,
          error: error.response?.data?.message || "Failed to terminate session",
        }
      }
    },

    async terminateAllSessions() {
      try {
        // Using this.$axios injected by pinia plugin

        // Get CSRF cookie first
        await this.getAxios().get("/sanctum/csrf-cookie")

        const response = await this.getAxios().delete("/api/v1/auth/sessions")
        return {
          success: true,
          message: response.data.message || "All sessions terminated successfully",
        }
      } catch (error) {
        return {
          success: false,
          error: error.response?.data?.message || "Failed to terminate all sessions",
        }
      }
    },

    async updateDriverStatus(newStatus) {
      if (this.isUpdatingStatus || !this.user || this.user.role !== "driver") {
        return { success: false, error: "Invalid operation" }
      }
      this.isUpdatingStatus = true

      try {
        // Add your API call here
        // Using this.$axios injected by pinia plugin
        const response = await this.getAxios().put(`/api/v1/fleet/drivers/${this.user.id}/status`, {
          status: newStatus,
        })
        if (response.data.status === "success") {
          this.user = {
            ...this.user,
            details: {
              ...this.user.details,
              status: newStatus,
            },
          }
          return { success: true, message: response.data.message || "Status updated successfully" }
        }

        return { success: false, error: response.data.message || "Failed to update status" }
      } catch (error) {
        console.error("Status update error:", error)
        return { success: false, error: "Failed to update status" }
      } finally {
        this.isUpdatingStatus = false
      }
    },

    // Check if the driver can change their status
    canChangeStatus() {
      if (!this.user || this.user.role !== "driver") return false
      console.log(this.user.details.status)

      // Only allow status change if current status is 'available' or 'off_duty'
      return ["available", "off_duty"].includes(this.user.details.status)
    },

    /**
     * Request OTP for phone login
     */
    async requestOtp(phone, purpose = "login") {
      try {
        const response = await this.getAxios().post("/api/v1/auth/otp/request", {
          phone: phone.replace(/\s/g, ""),
          purpose,
        })
        return response.data
      } catch (error) {
        throw error
      }
    },

    /**
     * Verify OTP and login
     */
    async verifyOtpLogin(phone, otpCode, grantType = "cookie") {
      try {
        const response = await this.getAxios().post("/api/v1/auth/otp/verify", {
          phone: phone.replace(/\s/g, ""),
          otp_code: otpCode,
          grant_type: grantType,
          device_name: navigator?.userAgent || "web",
        })

        if (response.data.success) {
          const { user } = response.data
          this.setAuthData({ user })
          return { success: true, user }
        }

        return { success: false, error: "Login failed" }
      } catch (error) {
        throw error
      }
    },

    /**
     * Verify phone for registration or phone change
     */
    async verifyPhone(phone, otpCode, purpose) {
      try {
        const response = await this.getAxios().post("/api/v1/auth/otp/verify-phone", {
          phone: phone.replace(/\s/g, ""),
          otp_code: otpCode,
          purpose,
        })
        return response.data
      } catch (error) {
        throw error
      }
    },

    async switchDriverWorkspace(workforceType) {
      if (!workforceType || this.isSwitchingWorkspace) {
        return { success: false, error: "Invalid workforce profile." }
      }

      this.isSwitchingWorkspace = true

      try {
        await this.getAxios().get("/sanctum/csrf-cookie")

        const response = await this.getAxios().post("/api/v1/driver/me/workforce/switch", {
          workforce_type: workforceType,
        })

        if (response.data?.data) {
          this.applyDriverWorkforcePayload(response.data.data)
        }

        return {
          success: true,
          message: response.data?.message || "Workspace updated successfully.",
          data: response.data?.data || null,
        }
      } catch (error) {
        const payload = error.response?.data?.data
        if (payload) {
          this.applyDriverWorkforcePayload(payload)
        }

        return {
          success: false,
          error: error.response?.data?.message || "Failed to switch workspace.",
          data: payload || null,
        }
      } finally {
        this.isSwitchingWorkspace = false
      }
    },
  },

  persist: true,
})
